import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllResponses, updateResponseStatus } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import './ModeratorPage.css';

const ZIRVE_LOGO = 'https://musterideneyimizirvesi.com/images/zirve-logo.jpg';

// Only two states: pending (beklemede) and published (yayında)
const STATUS_LABELS = {
  pending:   { label: 'Bekliyor',  color: '#f39c12' },
  published: { label: 'Yayında',   color: '#27ae60' },
};

const FILTER_OPTIONS = [
  { value: 'all',       label: 'Tümü' },
  { value: 'pending',   label: 'Bekleyenler' },
  { value: 'published', label: 'Yayında' },
];

export default function ModeratorPage() {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updating, setUpdating] = useState(null);
  const { isAuthenticated, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [isAuthenticated, authLoading, navigate]);

  const fetchResponses = useCallback(async () => {
    try {
      const res = await getAllResponses();
      setResponses(res.data);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchResponses();
      const interval = setInterval(fetchResponses, 15000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchResponses]);

  const handleStatusChange = async (id, newStatus) => {
    setUpdating(id);
    try {
      const updated = await updateResponseStatus(id, newStatus);
      setResponses((prev) =>
        prev.map((r) => (r.id === id ? { ...r, ...updated.data } : r))
      );
    } catch (err) {
      console.error('Update error:', err);
    } finally {
      setUpdating(null);
    }
  };

  const filtered = responses.filter(
    (r) => filter === 'all' || r.status === filter
  );

  const counts = {
    all:       responses.length,
    pending:   responses.filter((r) => r.status === 'pending').length,
    published: responses.filter((r) => r.status === 'published').length,
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (authLoading) return null;

  return (
    <div className="mod-page">
      {/* Sidebar */}
      <aside className="mod-sidebar">
        <div className="sidebar-logo">
          <img src={ZIRVE_LOGO} alt="Müşteri Deneyimi Zirvesi" className="sidebar-zirve-logo" />
        </div>

        <nav className="sidebar-nav">
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`nav-item ${filter === opt.value ? 'active' : ''}`}
              onClick={() => setFilter(opt.value)}
            >
              <span>{opt.label}</span>
              <span className="nav-count">{counts[opt.value]}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-actions">
          <button className="btn-refresh" onClick={fetchResponses}>↻ Yenile</button>
          <a href="/feed" target="_blank" rel="noopener noreferrer" className="btn-feed-link">
            ▶ Akışı Görüntüle
          </a>
          <button className="btn-logout" onClick={logout}>Çıkış Yap</button>
        </div>

        <div className="sidebar-footer">
          <span>
            Powered by{' '}
            <a
              href="https://cloud4feed.com"
              target="_blank"
              rel="noopener noreferrer"
              className="c4f-link"
            >
              Cloud4Feed
            </a>
          </span>
        </div>
      </aside>

      {/* Main content */}
      <main className="mod-main">
        <div className="mod-header">
          <h1>Yanıtlar</h1>
          <span className="mod-header-sub">{filtered.length} yanıt</span>
        </div>

        {loading ? (
          <div className="mod-loading">
            <div className="big-spinner" />
            <span>Yükleniyor...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mod-empty">
            <div className="empty-icon">📭</div>
            <p>Bu filtrede henüz yanıt yok.</p>
          </div>
        ) : (
          <div className="responses-list">
            {filtered.map((r) => (
              <div key={r.id} className={`response-card status-${r.status}`}>
                <div className="response-top">
                  <span className="status-badge" style={{ color: STATUS_LABELS[r.status]?.color }}>
                    {STATUS_LABELS[r.status]?.label}
                  </span>
                  <span className="response-date">{formatDate(r.createdAt)}</span>
                </div>

                <p className="response-text">{r.text}</p>

                <div className="response-actions">
                  {r.status === 'pending' && (
                    <button
                      className="btn-publish"
                      onClick={() => handleStatusChange(r.id, 'published')}
                      disabled={updating === r.id}
                    >
                      {updating === r.id ? '...' : '✓ Yayınla'}
                    </button>
                  )}
                  {r.status === 'published' && (
                    <button
                      className="btn-unpublish"
                      onClick={() => handleStatusChange(r.id, 'pending')}
                      disabled={updating === r.id}
                    >
                      {updating === r.id ? '...' : '✕ Yayından Kaldır'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
